import React, { useState, useEffect } from 'react';
import { Home, CreditCard, Clock, CheckCircle, AlertTriangle, FileText, Download, Ban } from 'lucide-react';
import { Card, Button, Table, Badge, Loading, Modal } from '../components/ui';
import { departamentosService, sancionesService } from '../services/api';
import { formatMoney, formatDate } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const MiDepartamentoPage = () => {
  const { usuario } = useAuth();
  const [loading, setLoading] = useState(true);
  const [departamento, setDepartamento] = useState(null);
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [sanciones, setSanciones] = useState([]);
  const [deudaModalOpen, setDeudaModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Obtener departamento del usuario
      if (usuario?.departamento_id) {
        const [deptRes, estadoRes, sancionesRes] = await Promise.all([
          departamentosService.getById(usuario.departamento_id),
          departamentosService.getEstadoCuenta(usuario.departamento_id),
          sancionesService.getByDepartamento(usuario.departamento_id)
        ]);
        setDepartamento(deptRes.data.data);
        setEstadoCuenta(estadoRes.data.data);
        setPagos(estadoRes.data.data?.pagos || []);
        setSanciones(sancionesRes.data.data?.sanciones || []);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loading size="lg" text="Cargando..." />
      </div>
    );
  }

  if (!departamento) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <Home size={48} className="text-slate-600 mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Sin departamento asignado</h2>
        <p className="text-slate-400">Tu cuenta no tiene un departamento vinculado.</p>
        <p className="text-slate-500 text-sm mt-2">Contacta al administrador.</p>
      </div>
    );
  }

  const columns = [
    { header: 'Fecha', render: (row) => formatDate(row.fecha) },
    { header: 'Concepto', accessor: 'concepto' },
    { header: 'Mes', render: (row) => row.mes_correspondiente || '-' },
    { 
      header: 'Estado', 
      render: (row) => (
        <Badge variant={row.estado === 'PAGADO' ? 'success' : 'warning'}>
          {row.estado}
        </Badge>
      )
    },
    { 
      header: 'Monto', 
      render: (row) => (
        <span className={`font-semibold ${row.estado === 'PAGADO' ? 'text-emerald-400' : 'text-amber-400'}`}>
          {formatMoney(row.monto)}
        </span>
      )
    }
  ];

  const deudaRecibos = estadoCuenta?.deuda_total || 0;
  const deudaSanciones = sanciones.filter(s => s.estado === 'PENDIENTE').reduce((sum, s) => sum + parseFloat(s.monto), 0);
  const deudaTotal = deudaRecibos + deudaSanciones;
  const pagosAlDia = estadoCuenta?.pagos_al_dia || 0;
  const mesesPendientes = estadoCuenta?.meses_pendientes || 0;
  const sancionesPendientes = sanciones.filter(s => s.estado === 'PENDIENTE').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Mi Departamento</h1>
        <p className="text-slate-400">Estado de cuenta y pagos</p>
      </div>

      {/* Info Departamento */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600">
            <Home size={32} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-white">Departamento {departamento.numero}</h2>
              {deudaTotal === 0 ? (
                <Badge variant="success">Al día</Badge>
              ) : (
                <button onClick={() => setDeudaModalOpen(true)}>
                  <Badge variant="danger" className="cursor-pointer hover:opacity-80 transition-opacity">
                    Con deuda
                  </Badge>
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Propietario</p>
                <p className="text-white font-medium">{departamento.propietario}</p>
              </div>
              <div>
                <p className="text-slate-500">Piso</p>
                <p className="text-white font-medium">{departamento.piso || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Área</p>
                <p className="text-white font-medium">{departamento.area_m2 ? `${departamento.area_m2} m²` : '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Cuota mensual</p>
                <p className="text-emerald-400 font-bold">{formatMoney(departamento.cuota_mensual || 350)}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${deudaTotal > 0 ? 'bg-rose-500/20' : 'bg-emerald-500/20'}`}>
              {deudaTotal > 0 ? (
                <AlertTriangle className="text-rose-400" size={24} />
              ) : (
                <CheckCircle className="text-emerald-400" size={24} />
              )}
            </div>
            <div>
              <p className="text-sm text-slate-400">Deuda Total</p>
              <p className={`text-2xl font-bold ${deudaTotal > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                {formatMoney(deudaTotal)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${mesesPendientes > 0 ? 'bg-amber-500/20' : 'bg-emerald-500/20'}`}>
              <Clock className={mesesPendientes > 0 ? 'text-amber-400' : 'text-emerald-400'} size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Recibos Pend.</p>
              <p className={`text-2xl font-bold ${mesesPendientes > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {mesesPendientes}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${sancionesPendientes > 0 ? 'bg-rose-500/20' : 'bg-emerald-500/20'}`}>
              <Ban className={sancionesPendientes > 0 ? 'text-rose-400' : 'text-emerald-400'} size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Sanciones</p>
              <p className={`text-2xl font-bold ${sancionesPendientes > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                {sancionesPendientes}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-500/20">
              <CreditCard className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Pagos Realizados</p>
              <p className="text-2xl font-bold text-white">{pagosAlDia}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Alerta de deuda */}
      {deudaTotal > 0 && (
        <Card className="p-4 border-amber-500/50 bg-amber-500/10">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-amber-400" size={24} />
            <div>
              <p className="text-amber-400 font-medium">Tienes pagos pendientes</p>
              <p className="text-slate-400 text-sm">
                Por favor regulariza tu situación. Contacta a tesorería para coordinar el pago.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Historial de Pagos */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Historial de Pagos</h3>
          <Button variant="secondary" size="sm" icon={Download}>
            Exportar
          </Button>
        </div>
        <Table 
          columns={columns} 
          data={pagos} 
          emptyMessage="No hay pagos registrados"
        />
      </Card>

      {/* Sanciones */}
      {sanciones.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Ban size={20} className="text-rose-400" />
              Sanciones e Infracciones
            </h3>
          </div>
          <div className="divide-y divide-slate-700">
            {sanciones.map((sancion, idx) => (
              <div key={idx} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={
                        sancion.tipo === 'LEVE' ? 'warning' : 
                        sancion.tipo === 'GRAVE' ? 'danger' : 'danger'
                      }>
                        {sancion.tipo}
                      </Badge>
                      <Badge variant={
                        sancion.estado === 'PAGADA' ? 'success' : 
                        sancion.estado === 'ANULADA' ? 'default' : 'warning'
                      }>
                        {sancion.estado}
                      </Badge>
                    </div>
                    <p className="text-white font-medium">{sancion.motivo}</p>
                    <p className="text-slate-400 text-sm mt-1">{formatDate(sancion.fecha)}</p>
                    {sancion.observaciones && (
                      <p className="text-slate-500 text-sm mt-2">{sancion.observaciones}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className={`text-lg font-bold ${sancion.estado === 'PAGADA' ? 'text-slate-400 line-through' : 'text-rose-400'}`}>
                      {formatMoney(sancion.monto)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Información de pago */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <FileText size={20} />
          Información de Pago
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-400">Banco</span>
              <span className="text-white font-medium">BCP / Interbank</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">N° Cuenta BCP</span>
              <span className="text-white font-medium font-mono">123-456789-0-12</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">N° Cuenta Interbank</span>
              <span className="text-white font-medium font-mono">789-123456789-01</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-400">CCI BCP</span>
              <span className="text-white font-medium font-mono">002-123-456789012345-12</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Titular</span>
              <span className="text-white font-medium">Junta de Propietarios Edificio XYZ</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Fecha límite de pago</span>
              <span className="text-amber-400 font-medium">Día 10 de cada mes</span>
            </div>
          </div>
        </div>
        <p className="text-slate-500 text-sm mt-4">
          * Envía tu comprobante de pago al WhatsApp de administración o déjalo en portería.
        </p>
      </Card>

      {/* Modal Detalle de Deuda */}
      <Modal isOpen={deudaModalOpen} onClose={() => setDeudaModalOpen(false)} title="Detalle de Deuda" size="lg">
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-rose-500/10 border border-rose-500/30">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-rose-400" size={24} />
              <span className="text-white font-medium">Deuda Total</span>
            </div>
            <span className="text-2xl font-bold text-rose-400">{formatMoney(deudaTotal)}</span>
          </div>
          
          <h4 className="text-white font-semibold">Recibos Pendientes</h4>
          
          {pagos.filter(p => p.estado === 'PENDIENTE').length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {pagos.filter(p => p.estado === 'PENDIENTE').map((recibo, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50 border border-slate-600">
                  <div>
                    <p className="text-white font-medium">{recibo.concepto}</p>
                    <p className="text-slate-400 text-sm">
                      {recibo.mes_correspondiente ? `Mes: ${recibo.mes_correspondiente}` : formatDate(recibo.fecha)}
                    </p>
                  </div>
                  <span className="text-amber-400 font-bold">{formatMoney(recibo.monto)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-center py-4">No hay recibos pendientes</p>
          )}
          
          <div className="pt-4 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              Para realizar el pago, usa la información bancaria de abajo o ve a <strong className="text-emerald-400">Mis Pagos</strong> para registrar tu pago.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MiDepartamentoPage;